/**
 * CandleScript - Static Code Analyzer
 * Validates user code BEFORE execution to catch dangerous patterns.
 */

const FORBIDDEN_PATTERNS = [
    // Globals access
    { pattern: /\bwindow\b/, message: 'Access to "window" is not allowed' },
    { pattern: /\bdocument\b/, message: 'Access to "document" is not allowed' },
    { pattern: /\bglobalThis\b/, message: 'Access to "globalThis" is not allowed' },
    { pattern: /\bself\b/, message: 'Access to "self" is not allowed' },
    { pattern: /(?:^|[^.:\w])location\s*[.[=]/, message: 'Access to "location" is not allowed' },
    { pattern: /\bnavigator\b/, message: 'Access to "navigator" is not allowed' },
    { pattern: /\blocalStorage\b/, message: 'Access to "localStorage" is not allowed' },
    { pattern: /\bsessionStorage\b/, message: 'Access to "sessionStorage" is not allowed' },
    { pattern: /\bindexedDB\b/, message: 'Access to "indexedDB" is not allowed' },

    // Network
    { pattern: /\bfetch\b/, message: 'Network access (fetch) is not allowed' },
    { pattern: /\bXMLHttpRequest\b/, message: 'Network access (XMLHttpRequest) is not allowed' },
    { pattern: /\bWebSocket\b/, message: 'WebSocket access is not allowed' },
    { pattern: /\bimportScripts\b/, message: 'importScripts is not allowed' },

    // Code generation
    { pattern: /\beval\s*\(/, message: 'eval() is not allowed' },
    { pattern: /\bnew\s+Function\s*\(/, message: 'new Function() is not allowed' },
    { pattern: /\bsetTimeout\b/, message: 'setTimeout is not allowed' },
    { pattern: /\bsetInterval\b/, message: 'setInterval is not allowed' },

    // Prototype pollution
    { pattern: /__proto__/, message: '__proto__ access is not allowed' },
    { pattern: /\bconstructor\s*\[/, message: 'constructor[] access is not allowed' },
    { pattern: /Object\s*\.\s*defineProperty/, message: 'Object.defineProperty is not allowed' },
    { pattern: /Object\s*\.\s*setPrototypeOf/, message: 'Object.setPrototypeOf is not allowed' },
    { pattern: /Object\s*\.\s*assign\s*\(\s*Object\.prototype/, message: 'Modifying Object.prototype is not allowed' },
    { pattern: /\.prototype\s*=/, message: 'Prototype assignment is not allowed' },
    { pattern: /\.prototype\s*\[/, message: 'Prototype bracket access is not allowed' },

    // Module system
    { pattern: /\brequire\s*\(/, message: 'require() is not allowed' },
    { pattern: /\bimport\s*\(/, message: 'Dynamic import() is not allowed' },
    { pattern: /\bimport\s+/, message: 'import statements are not allowed' },
    { pattern: /\bexport\s+/, message: 'export statements are not allowed' },

    // Process / system
    { pattern: /\bprocess\b/, message: 'Access to "process" is not allowed' },
    { pattern: /\b__dirname\b/, message: 'Access to "__dirname" is not allowed' },
    { pattern: /\b__filename\b/, message: 'Access to "__filename" is not allowed' },
];

/**
 * Detect infinite loop patterns (heuristic)
 */
const LOOP_PATTERNS = [
    { pattern: /while\s*\(\s*true\s*\)/, message: 'while(true) loops are not allowed' },
    { pattern: /while\s*\(\s*1\s*\)/, message: 'while(1) loops are not allowed' },
    { pattern: /for\s*\(\s*;\s*;\s*\)/, message: 'Infinite for(;;) loops are not allowed' },
    { pattern: /while\s*\(\s*!\s*false\s*\)/, message: 'while(!false) loops are not allowed' },
    { pattern: /while\s*\(\s*!\s*0\s*\)/, message: 'while(!0) loops are not allowed' },
];

/**
 * Check recursion depth by counting function definitions
 */
function checkRecursionRisk(code) {
    const funcNames = [];
    const funcRegex = /function\s+(\w+)\s*\(/g;
    let match;
    while ((match = funcRegex.exec(code)) !== null) {
        funcNames.push(match[1]);
    }

    // Check if any function calls itself
    const warnings = [];
    for (const name of funcNames) {
        if (name === 'indicator') continue; // Skip the main function
        const callRegex = new RegExp(`\\b${name}\\s*\\(`, 'g');
        const calls = code.match(callRegex);
        if (calls && calls.length > 1) {
            warnings.push({
                type: 'warning',
                message: `Function "${name}" appears to be recursive. Deep recursion may cause timeout.`,
            });
        }
    }
    return warnings;
}

/**
 * Estimate code complexity
 */
function checkComplexity(code) {
    const warnings = [];

    // Count nested loops
    let depth = 0;
    let maxDepth = 0;
    const lines = code.split('\n');
    for (const line of lines) {
        const trimmed = line.trim();
        if (/^(for|while)\s*\(/.test(trimmed)) {
            depth++;
            maxDepth = Math.max(maxDepth, depth);
        }
        if (trimmed === '}') {
            depth = Math.max(0, depth - 1);
        }
    }

    if (maxDepth >= 3) {
        warnings.push({
            type: 'warning',
            message: `Deeply nested loops detected (depth: ${maxDepth}). This may cause performance issues with large datasets.`,
        });
    }

    // Check code length
    if (code.length > 50000) {
        warnings.push({
            type: 'error',
            message: 'Script exceeds maximum allowed size (50KB).',
        });
    }

    return warnings;
}

/**
 * Strip comments and strings to avoid false positives
 */
function stripCommentsAndStrings(code) {
    // Remove single-line comments
    let stripped = code.replace(/\/\/.*$/gm, '');
    // Remove multi-line comments
    stripped = stripped.replace(/\/\*[\s\S]*?\*\//g, '');
    // Remove template literals
    stripped = stripped.replace(/`[^`]*`/g, '""');
    // Remove strings
    stripped = stripped.replace(/'[^']*'/g, '""');
    stripped = stripped.replace(/"[^"]*"/g, '""');
    return stripped;
}

/**
 * Validate user code before execution
 * @param {string} code - User's indicator code
 * @returns {{ valid: boolean, errors: Array, warnings: Array }}
 */
export function validateCode(code) {
    const errors = [];
    const warnings = [];

    if (!code || typeof code !== 'string') {
        return { valid: false, errors: [{ type: 'error', message: 'No code provided' }], warnings: [] };
    }

    const stripped = stripCommentsAndStrings(code);

    // Check forbidden patterns
    for (const { pattern, message } of FORBIDDEN_PATTERNS) {
        if (pattern.test(stripped)) {
            errors.push({ type: 'error', message, pattern: pattern.toString() });
        }
    }

    // Check infinite loop patterns
    for (const { pattern, message } of LOOP_PATTERNS) {
        if (pattern.test(stripped)) {
            errors.push({ type: 'error', message });
        }
    }

    // Check recursion risk
    warnings.push(...checkRecursionRisk(stripped));

    // Check complexity
    const complexityIssues = checkComplexity(code);
    for (const issue of complexityIssues) {
        if (issue.type === 'error') errors.push(issue);
        else warnings.push(issue);
    }

    // Check that indicator function exists
    if (!/function\s+indicator\s*\(/.test(stripped)) {
        errors.push({
            type: 'error',
            message: 'Script must define a function named "indicator(input, options)"',
        });
    }

    return {
        valid: errors.length === 0,
        errors,
        warnings,
    };
}

export default validateCode;
